import { useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Toast, CardLayout, LoadingOverlay, Text, spacing, Button, colors } from '@pletly/ui';
import { RootStackScreenProps } from '../../../navigation/types';
import { withTranslation } from '../../../locales/hoc';
import { TeamForm, TeamFormActions, useTeamForm } from '../components/TeamForm';
import { useTeamData } from '../components/useTeamData';
import { useUpdateTeamMutation } from './components/__generated__/updateTeam.mutation.generated';
import { OrganisationTeamRole } from '../../../__generated__/types';
import { useDeleteTeam } from './components/useDeleteTeam';

export type EditTeamScreenParams = {
	uuid: string;
	organisationUuid: string;
};

type Props = RootStackScreenProps<'EditTeam'>;

const EditTeamPage = ({
	navigation: { replace, goBack },
	route: {
		params: { uuid, organisationUuid },
	},
}: Props) => {
	const { t } = useTranslation();

	const [updateTeam, { loading: saving }] = useUpdateTeamMutation();

	const teamData = useTeamData({
		teamUuid: uuid,
		organisationUuid,
	});

	const formMethods = useTeamForm({
		defaultValues: useMemo(() => {
			if (teamData.state === 'success') {
				const { name, postsScreeningEnabled } = teamData.currentTeam;

				return { name, postsScreening: postsScreeningEnabled };
			}

			return { name: '', postsScreening: false };
		}, [teamData]),
	});

	const { canDelete, handleDeletePress } = useDeleteTeam({
		uuid,
		organisationUuid,
		team: teamData.state === 'success' ? teamData.currentTeam : null,
	});

	useEffect(() => {
		if (teamData.state !== 'success') {
			return;
		}

		const { name, postsScreeningEnabled } = teamData.currentTeam;
		formMethods.reset({ name, postsScreening: postsScreeningEnabled });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [teamData.state, formMethods.reset]);

	const handleSubmit = useCallback(async () => {
		if (saving) {
			return;
		}
		const { name, postsScreening } = formMethods.getValues();
		try {
			await updateTeam({
				variables: { data: { teamUuid: uuid, name, postsScreeningEnabled: postsScreening } },
			});
			Toast.show({ type: 'success', text1: t('toasts.success.team-updated') });
			replace('OrganisationTeam', { teamUuid: uuid, organisationUuid });
		} catch (error) {
			Toast.show({ type: 'error', text1: t('toasts.error.something-wrong') });
		}
	}, [formMethods, t, saving, updateTeam, uuid, organisationUuid, replace]);

	if (teamData.state === 'error') {
		return <Text>{teamData.errorMessage}</Text>;
	}

	const disabledTogglePostsScreening =
		teamData.state !== 'success' ||
		teamData.currentTeam.members.caregivers.every(
			(caregiver) => caregiver.teamRole !== OrganisationTeamRole.Admin,
		);

	return (
		<CardLayout title={t('teams.edit-team')}>
			<View style={styles.pageContent}>
				<LoadingOverlay show={teamData.state === 'loading' || saving} />

				<TeamForm
					formMethods={formMethods}
					type="edit"
					disabledTogglePostsScreening={disabledTogglePostsScreening}
				/>

				{canDelete && (
					<View style={styles.deleteSection}>
						<Text category="p2">{t('teams.delete.delete-team')}</Text>
						<Text category="c1" status="warning">
							{t('teams.delete.label-description')}
						</Text>
						<Button
							color="danger"
							onPress={handleDeletePress}
							size="small"
							variant="filled"
							style={styles.deleteButton}
						>
							{t('teams.delete.delete-team')}
						</Button>
					</View>
				)}

				<TeamFormActions onCancel={goBack} onSubmit={formMethods.handleSubmit(handleSubmit)} />
			</View>
		</CardLayout>
	);
};

const styles = StyleSheet.create({
	pageContent: {
		gap: spacing(4),
	},
	deleteSection: {
		borderRadius: 8,
		borderColor: colors.neutral,
		padding: spacing(2),
		borderWidth: 1,
		gap: spacing(),
	},
	deleteButton: {
		marginTop: spacing(),
		alignSelf: 'flex-start',
	},
});

export const EditTeam = withTranslation(EditTeamPage);
